import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, FileText, Gauge, ShieldCheck, Sparkles, FolderLock, ArrowRight } from "lucide-react";
import { Logo, ProductMark, IsoCred } from "@/components/Brand";
import { PlanCards, SprintCard, CycleToggle } from "@/components/PlanCards";
import { api } from "@/lib/api";
import { PublicFooter } from "@/components/PublicLayout";
import { ThemeToggle } from "@/context/ThemeContext";
import { LegalText } from "@/components/LegalModal";
import { TERMS, PRIVACY, LEGAL_VERSION } from "@/content/legal";

const FEATURES = [
  [ClipboardCheck, "Master Accreditation Tracker", "Every ISO 15189:2022 clause (4–8, Annex A POCT) mapped to NABL 112A criteria and 112B guidance."],
  [Gauge, "Readiness Score", "A transparent five-pillar score: documentation, approval, implementation, evidence and verification."],
  [Sparkles, "One-click AI Documents", "Generate SOPs, policies, formats and plans from clause context and your lab profile."],
  [FolderLock, "Evidence Repository", "Private, versioned evidence linked to clauses with verification status."],
  [FileText, "Document Control", "Draft → Review → Approve → Effective with full version and approval history."],
  [ShieldCheck, "Audits, CAPA & Risk", "Internal audits, nonconformities, equipment, personnel, risk and quality indicators."],
];

function PublicPricing() {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [cycle, setCycle] = useState("annual");
  useEffect(() => { api.get("/pricing").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-6 py-16" data-testid="landing-pricing">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div><div className="qt-eyebrow mb-2">Pricing</div><h2 className="font-display text-3xl font-extrabold text-[#172238]">Plans that grow with your laboratory</h2>
          <p className="text-sm text-[#5A6B87] mt-1">Per laboratory site. Start free: try every feature with a limited allowance, then subscribe. GST extra.</p></div>
        <CycleToggle cycle={cycle} setCycle={setCycle} />
      </div>
      <PlanCards pricing={p} cycle={cycle} onSelect={() => nav("/signup")} cta={(x) => (x.checkout ? "Start free trial" : "Start trial & talk to us")} />
      <div className="mt-6"><SprintCard s={p.launch_sprint} onBuy={() => nav("/signup")} cta="Get started" /></div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <ProductMark />
          <div className="flex gap-1.5 sm:gap-3 items-center shrink-0">
            <a href="#pricing" className="hidden md:inline text-sm font-medium text-[#3B4A63] hover:text-[#48217A] px-2" data-testid="landing-pricing-link">Pricing</a>
            <ThemeToggle testid="landing-theme-toggle" />
            <Button variant="ghost" asChild className="hidden min-[385px]:inline-flex px-2.5 sm:px-4" data-testid="landing-login-btn"><Link to="/login">Sign in</Link></Button>
            <Button asChild className="bg-[#48217A] hover:bg-[#3B1A66] px-3 sm:px-4" data-testid="landing-signup-btn"><Link to="/signup">Get started</Link></Button>
          </div>
        </div>
      </header>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <div className="fade-up">
          <div className="qt-eyebrow mb-4">ISO 15189:2022 Accreditation</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#172238] leading-[1.05]">
            Every Clause. Every Document. <span className="text-[#48217A]">Every Step</span> Towards Accreditation.
          </h1>
          <p className="mt-5 text-base text-[#5A6B87] max-w-xl"><IsoCred /> by Nirogyam is your laboratory's central workspace for understanding requirements, closing gaps, generating controlled documents and tracking how close you are to accreditation.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" asChild className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid="hero-signup-btn"><Link to="/signup">Create lab workspace <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            <Button size="lg" variant="outline" asChild data-testid="hero-login-btn"><Link to="/login">Sign in</Link></Button>
          </div>
        </div>
        <div className="relative">
          <img src="/brand/hero-lab.jpg" alt="Laboratory scientist" className="rounded-2xl h-[380px] w-full object-cover" />
          <div className="absolute -bottom-6 -left-6 qt-card p-4 w-56 bg-white">
            <div className="text-[11px] uppercase tracking-widest text-[#5A6B87]">Readiness score</div>
            <div className="font-display text-3xl font-extrabold text-[#48217A]">Five pillars</div>
            <div className="mt-2 h-2 rounded-full bg-[#F3EFFA] overflow-hidden"><div className="h-full w-2/3 bg-[#2E9B65]" /></div>
          </div>
        </div>
      </section>
      <section className="bg-[#F4F6F8] py-16 mt-8">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(([I, t, d]) => (
            <div key={t} className="qt-card p-5 hover:border-[#48217A]/30 transition-colors">
              <I className="h-6 w-6 text-[#48217A]" />
              <div className="font-display font-semibold mt-3">{t}</div>
              <p className="text-sm text-[#5A6B87] mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <PublicPricing />
      <section className="max-w-6xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center gap-6 justify-between">
        <Logo className="h-14" />
        <p className="text-sm text-[#5A6B87] max-w-xl"><IsoCred /> is a compliance-support tool. Scores are internal indicators and do not represent an official NABL accreditation decision.</p>
      </section>
      <PublicFooter />
    </div>
  );
}

export function LegalPage() {
  const { kind } = useParams();
  const terms = kind !== "privacy";
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b"><div className="max-w-3xl mx-auto px-6 h-16 flex items-center"><Link to="/"><ProductMark /></Link></div></header>
      <main className="flex-1 max-w-3xl mx-auto px-6 py-10" data-testid="legal-page">
        <h1 className="font-display text-3xl font-bold">{terms ? "Terms & Conditions" : "Privacy Policy"}</h1>
        <p className="text-sm text-[#5A6B87] mb-6">Nirogyam Diagnostics and Healthcare Pvt Ltd · {LEGAL_VERSION}</p>
        <LegalText sections={terms ? TERMS : PRIVACY} />
      </main>
      <PublicFooter />
    </div>
  );
}
